import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Calendar,
  Check,
  ChevronsUpDown,
  Megaphone,
  Mail,
  Phone,
  Users,
  MessageSquare,
  Linkedin,
  UserPlus,
  Sparkles,
  Pencil,
  Trash2,
  X,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ENGAGEMENT_OUTCOMES,
  ENGAGEMENT_TYPES,
  useAccountContacts,
  useConfirmProposedEngagementNote,
  useDeleteEngagementNote,
  useUpdateEngagementNote,
  type EngagementNote,
  type EngagementOutcome,
  type EngagementType,
} from "@/hooks/useEngagementNotes";

const TYPE_ICON: Record<EngagementType, React.ReactNode> = {
  outreach: <Megaphone className="h-3 w-3" />,
  email: <Mail className="h-3 w-3" />,
  call: <Phone className="h-3 w-3" />,
  meeting: <Users className="h-3 w-3" />,
  demo: <Sparkles className="h-3 w-3" />,
  linkedin: <Linkedin className="h-3 w-3" />,
  intro: <UserPlus className="h-3 w-3" />,
  other: <MessageSquare className="h-3 w-3" />,
};

const TYPE_COLOR: Record<EngagementType, string> = {
  outreach: "bg-blue-50 text-blue-700 border-blue-200",
  email: "bg-sky-50 text-sky-700 border-sky-200",
  call: "bg-amber-50 text-amber-700 border-amber-200",
  meeting: "bg-violet-50 text-violet-700 border-violet-200",
  demo: "bg-pink-50 text-pink-700 border-pink-200",
  linkedin: "bg-indigo-50 text-indigo-700 border-indigo-200",
  intro: "bg-emerald-50 text-emerald-700 border-emerald-200",
  other: "bg-muted text-muted-foreground border-border",
};

function formatDate(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function EngagementNoteRow({ note }: { note: EngagementNote }) {
  const update = useUpdateEngagementNote();
  const confirm = useConfirmProposedEngagementNote();
  const del = useDeleteEngagementNote();
  const { data: contacts = [] } = useAccountContacts(note.account_id);

  const [editing, setEditing] = useState(false);
  const [engagementType, setEngagementType] = useState<EngagementType>(note.engagement_type);
  const [engagementDate, setEngagementDate] = useState<string>(note.engagement_date);
  const [outcome, setOutcome] = useState<EngagementOutcome | null>(note.outcome);
  const [detail, setDetail] = useState<string>(note.detail ?? "");
  const [contactId, setContactId] = useState<string | null>(note.contact_id);
  const [attendeesText, setAttendeesText] = useState<string>((note.attendees ?? []).join(", "));
  const [contactPickerOpen, setContactPickerOpen] = useState(false);

  const activeContact = contacts.find((c) => c.id === contactId) || null;

  const handleSave = async () => {
    try {
      const attendees = attendeesText.split(",").map((s) => s.trim()).filter(Boolean);
      await update.mutateAsync({
        id: note.id,
        accountId: note.account_id,
        patch: {
          engagement_type: engagementType,
          engagement_date: engagementDate,
          outcome,
          detail: detail.trim() || null,
          attendees: attendees.length ? attendees : null,
          contact_id: contactId,
          is_proposed: false,
        },
      });
      toast.success("Updated");
      setEditing(false);
    } catch (e: any) {
      toast.error("Failed to update", { description: e?.message });
    }
  };

  const handleConfirm = async (acceptProposed: boolean) => {
    try {
      await confirm.mutateAsync({
        id: note.id,
        accountId: note.account_id,
        acceptProposedContact: acceptProposed,
      });
      toast.success(acceptProposed ? "Confirmed and attached contact" : "Confirmed");
    } catch (e: any) {
      toast.error("Failed to confirm", { description: e?.message });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this engagement note?")) return;
    try {
      await del.mutateAsync({ id: note.id, accountId: note.account_id });
      toast.success("Deleted");
    } catch (e: any) {
      toast.error("Failed to delete", { description: e?.message });
    }
  };

  const isProposed = note.is_proposed;

  return (
    <div
      className={cn(
        "rounded-md border bg-card p-3 space-y-2",
        isProposed && "border-dashed border-amber-300 bg-amber-50/40"
      )}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={cn(
            "text-[11px] inline-flex items-center gap-1 capitalize",
            TYPE_COLOR[note.engagement_type]
          )}
        >
          {TYPE_ICON[note.engagement_type]}
          {note.engagement_type}
        </Badge>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {formatDate(note.engagement_date)}
        </span>
        {note.outcome && (
          <Badge variant="secondary" className="text-[10px] capitalize">
            {note.outcome.replace(/_/g, " ")}
          </Badge>
        )}
        {note.source === "migrated_from_notes" && (
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600 bg-slate-50">
            migrated
          </Badge>
        )}
        {isProposed && (
          <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-100">
            proposed — confirm
          </Badge>
        )}
        <div className="ml-auto flex gap-1">
          {!editing ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => setEditing(true)}
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-destructive"
                onClick={handleDelete}
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => setEditing(false)}
                title="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="default"
                className="h-7 px-2"
                onClick={handleSave}
                disabled={update.isPending}
                title="Save"
              >
                <Save className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Contact + body — read mode */}
      {!editing && (
        <>
          <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
            {note.contact ? (
              <span>
                <span className="font-medium text-foreground">{note.contact.full_name || "—"}</span>
                {note.contact.job_title && ` · ${note.contact.job_title}`}
              </span>
            ) : note.proposed_contact ? (
              <>
                <span className="italic">
                  Proposed:&nbsp;
                  <span className="font-medium text-foreground">{note.proposed_contact.full_name}</span>
                  {note.proposed_contact.job_title && ` · ${note.proposed_contact.job_title}`}
                </span>
                {note.proposed_contact_confidence != null && (
                  <span className="text-[10px]">
                    ({Math.round(note.proposed_contact_confidence * 100)}% match)
                  </span>
                )}
              </>
            ) : (
              <span className="italic">No specific contact</span>
            )}
            {note.attendees && note.attendees.length > 0 && (
              <span>· attendees: {note.attendees.join(", ")}</span>
            )}
          </div>

          {note.detail && <p className="text-sm whitespace-pre-wrap">{note.detail}</p>}

          {/* Confirm controls — only when proposed */}
          {isProposed && (
            <div className="flex flex-wrap gap-2 pt-1">
              {note.proposed_contact && (
                <Button
                  size="sm"
                  variant="default"
                  className="h-7"
                  onClick={() => handleConfirm(true)}
                  disabled={confirm.isPending}
                >
                  Confirm &amp; attach {note.proposed_contact.full_name}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() => handleConfirm(false)}
                disabled={confirm.isPending}
              >
                Confirm without contact
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={() => setEditing(true)}
              >
                Edit first
              </Button>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground">
            {note.created_by_name ? `Logged by ${note.created_by_name}` : "Logged"}
            {" · "}
            {new Date(note.created_at).toLocaleDateString()}
          </p>
        </>
      )}

      {/* Edit mode */}
      {editing && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={engagementDate}
              onChange={(e) => setEngagementDate(e.target.value)}
            />
            <Select
              value={engagementType}
              onValueChange={(v) => setEngagementType(v as EngagementType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENGAGEMENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Popover open={contactPickerOpen} onOpenChange={setContactPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className={cn(
                  "w-full justify-between font-normal",
                  !activeContact && "text-muted-foreground"
                )}
              >
                {activeContact?.full_name || "No specific contact"}
                <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
              <Command>
                <CommandInput placeholder="Search contacts…" />
                <CommandList>
                  <CommandEmpty>No matching contact.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setContactId(null);
                        setContactPickerOpen(false);
                      }}
                    >
                      <span className="text-muted-foreground italic">No specific contact</span>
                    </CommandItem>
                    {contacts.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={(c.full_name || "") + " " + (c.job_title || "")}
                        onSelect={() => {
                          setContactId(c.id);
                          setContactPickerOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3.5 w-3.5",
                            contactId === c.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span className="text-sm">{c.full_name || "—"}</span>
                          {c.job_title && (
                            <span className="text-[11px] text-muted-foreground">{c.job_title}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Input
            placeholder="Attendees (comma-separated)"
            value={attendeesText}
            onChange={(e) => setAttendeesText(e.target.value)}
          />

          <Select
            value={outcome ?? "none"}
            onValueChange={(v) => setOutcome(v === "none" ? null : (v as EngagementOutcome))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Outcome" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— no outcome —</SelectItem>
              {ENGAGEMENT_OUTCOMES.map((o) => (
                <SelectItem key={o} value={o} className="capitalize">
                  {o.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Textarea
            rows={3}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Detail"
          />
        </div>
      )}
    </div>
  );
}
